import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import moment from 'moment';
import { pdf } from '@react-pdf/renderer';
import PdfTemplates from '../../../components/PdfTemplates';
import { publicVoucherDetails } from '../../../controllers/V1/winnersControllers';
import { ASSETS_BASE_URL } from '../../../config/constants';
import Loading from '../../../components/Loading/LoadingEffect';

const IMAGE_FIELDS = ['doc', 'order_slip', 'redeem_slip', 'other_attachment', 'signature', 'images', 'images2'];

const resolveImageField = (imageData, fieldName) => {
  if (!imageData) return null;

  const imageUrl = typeof imageData === 'string' ? imageData : imageData[fieldName] || imageData;
  if (!imageUrl) return null;

  if (imageUrl.startsWith('data:') || imageUrl.startsWith('http')) {
    return {
      ...(typeof imageData === 'object' ? imageData : {}),
      [fieldName]: imageUrl,
    };
  }

  const cleanPath = imageUrl.replace(/^\.\//, '');
  return {
    ...(typeof imageData === 'object' ? imageData : {}),
    [fieldName]: `${ASSETS_BASE_URL}${cleanPath}`,
  };
};

export const processPublicVoucherImages = (formData) => {
  if (!formData) return formData;

  const processedData = { ...formData };

  IMAGE_FIELDS.forEach((fieldName) => {
    if (processedData[fieldName]) {
      const processed = resolveImageField(processedData[fieldName], fieldName);
      if (processed) {
        processedData[fieldName] = processed;
      }
    }
  });

  return processedData;
};

const PublicPreview = () => {
  const { order_id: orderId } = useParams();
  const [voucherData, setVoucherData] = useState(null);
  const [processedData, setProcessedData] = useState(null);
  const [isLoading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    document.title = 'Winning Voucher';
  }, []);

  useEffect(() => {
    const fetchVoucher = async () => {
      if (!orderId) {
        setError('Voucher not found.');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const res = await publicVoucherDetails(orderId);
        if (res.status === true && res.result) {
          setVoucherData(res.result);
          setProcessedData(processPublicVoucherImages(res.result));
        } else {
          setError(res.message || 'Voucher not found.');
        }
      } catch (err) {
        setError('Unable to load voucher. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchVoucher();
  }, [orderId]);

  useEffect(() => {
    const downloadPdf = async () => {
      if (!processedData) return;

      try {
        const doc = <PdfTemplates voucherData={processedData} />;
        const asPdf = pdf(doc);
        const blob = await asPdf.toBlob();
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `Winning Voucher ${processedData?.order_no || orderId} - ${moment().format('DD-MM-YYYY')}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } catch (err) {
        console.error('Error generating PDF:', err);
        setError('Unable to generate PDF. Please try again later.');
      }
    };

    downloadPdf();
  }, [processedData, orderId]);

  if (error) {
    return (
      <div style={{ maxWidth: 600, margin: '80px auto', textAlign: 'center', padding: 24 }}>
        <h4>{error}</h4>
      </div>
    );
  }

  return (
    <>
      <Loading isLoading={isLoading} text="Please wait while your voucher is being prepared..." />
      {!isLoading && voucherData && (
        <div style={{ maxWidth: 600, margin: '40px auto', textAlign: 'center', padding: 24 }}>
          <p>Your voucher PDF download should start automatically.</p>
          <p style={{ color: '#666', fontSize: 14 }}>
            Ticket ID: <strong>{voucherData?.order_no || orderId}</strong>
          </p>
        </div>
      )}
    </>
  );
};

export default PublicPreview;
